
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { STRIPE_CONFIG, isStripeConfigured, WHITELISTED_EMAILS } from '../stripeConfig';

/**
 * Initiates a Stripe Checkout session.
 */
export async function createCheckoutSession(priceKey: string, userId: string, userEmail?: string): Promise<void> {
  const isReal = isStripeConfigured();
  const resolvedPriceId = STRIPE_CONFIG.PRICES[priceKey as keyof typeof STRIPE_CONFIG.PRICES] || priceKey;
  const isWhitelisted = userEmail && WHITELISTED_EMAILS.includes(userEmail.toLowerCase());
  
  console.group(`[Stripe Checkout] Attempting: ${priceKey.toUpperCase()}`);
  console.log("User Email:", userEmail);
  console.log("Whitelisted:", isWhitelisted ? "YES" : "NO");
  console.log("Price ID in Code:", resolvedPriceId);
  console.log("Target Mode:", isReal ? "REAL" : "SIMULATION");
  console.groupEnd();

  try {
    // 🛡️ SIGMA BYPASS: Whitelisted accounts get instant success
    if (isWhitelisted) {
      console.log("[Stripe] Whitelist bypass triggered. Redirecting to success.");
      window.location.href = `${window.location.origin}/?payment=success&tier=${priceKey}`;
      return;
    }

    if (!isReal) {
      simulateCheckoutRedirect(priceKey, "Using placeholder keys.");
      return;
    }

    const stripe = await loadStripe(STRIPE_CONFIG.PUBLISHABLE_KEY);
    if (!stripe) throw new Error("Stripe.js failed to load.");

    // Invoke the Supabase Edge Function
    const response = await supabase.functions.invoke('create-checkout-session', {
      body: { 
        priceId: resolvedPriceId, 
        userId,
        tierKey: priceKey,
        success_url: `${window.location.origin}/?payment=success&tier=${priceKey}`,
        cancel_url: `${window.location.origin}/?payment=cancel`
      }
    });

    const { data, error } = response;

    if (error) {
      console.error("[Stripe] Cloud Function rejected the request:", error);
      
      // Attempt to extract more detail from the error body
      let detail = "Edge Function returned a non-2xx status code";
      if (error.message?.includes('No such price')) {
        detail = "SECRET KEY MISMATCH: Your Supabase STRIPE_SECRET_KEY is likely still a TEST key, but you are sending a LIVE Price ID.";
      } else if (error.message) {
        detail = error.message;
      }
      
      simulateCheckoutRedirect(priceKey, detail);
      return;
    }

    if (data?.url) {
      window.location.href = data.url;
    } else {
      simulateCheckoutRedirect(priceKey, data?.error || "Server returned no URL.");
    }

  } catch (err: any) {
    console.error("[Stripe] Fatal App Crash:", err);
    simulateCheckoutRedirect(priceKey, `Fatal: ${err.message}`);
  }
}

/**
 * Opens the Stripe Customer Portal for subscription management.
 */
export async function openCustomerPortal(userId: string): Promise<void> {
  const isReal = isStripeConfigured();

  try {
    if (!isReal) {
      alert("SIMULATION: In a real app, this would open the Stripe Customer Portal for managing subscriptions.");
      return;
    }

    // Call the Edge Function with the 'portal' action
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { 
        userId,
        action: 'portal',
        return_url: `${window.location.origin}/store`
      }
    });

    // Handle Supabase invocation errors (like function not found or network failure)
    if (error) {
      console.error("[Supabase Function Error]", error);
      throw new Error(error.message || "Could not reach the payment server.");
    }

    // Handle Logic errors returned from the Edge Function (like missing customer)
    if (data?.error) {
      throw new Error(data.error);
    }

    if (data?.url) {
      window.location.href = data.url;
    } else {
      throw new Error("Stripe didn't return a portal URL.");
    }
  } catch (err: any) {
    console.error("[Stripe Portal Error]", err);
    
    // Extract the specific error message to help the user diagnose the issue
    const rawMessage = err.message || "";
    let friendlyMessage = "FAILED TO OPEN PORTAL.";

    if (rawMessage.includes("No Stripe customer found")) {
      friendlyMessage = "NO STRIPE CUSTOMER FOUND. YOU MUST MAKE A PURCHASE BEFORE YOU CAN MANAGE A SUBSCRIPTION.";
    } else if (rawMessage.includes("Missing STRIPE_SECRET_KEY")) {
      friendlyMessage = "STRIPE NOT CONFIGURED ON SERVER. PLEASE ADD YOUR STRIPE_SECRET_KEY TO SUPABASE SECRETS.";
    } else if (rawMessage.includes("fetch")) {
      friendlyMessage = "CONNECTION BLOCKED. CHECK YOUR INTERNET OR DISABLE AD-BLOCKERS.";
    } else {
      friendlyMessage = `PORTAL ERROR: ${rawMessage.toUpperCase()}`;
    }

    alert(friendlyMessage);
  }
}

/**
 * Diagnostic popup for debugging.
 */
function simulateCheckoutRedirect(priceId: string, reason: string) {
  setTimeout(() => {
    const message = `
🧠 BRAINROT BLAST - DIAGNOSTICS
-------------------------------------
Attempted ID: ${priceId.toUpperCase()}

⚠️ THE REQUEST FAILED
Reason: ${reason}

FIX STEPS:
1. Verify the STRIPE_SECRET_KEY in Supabase matches your current Mode (Live).
2. Ensure you have deployed the latest version of the Edge Function.
3. Refresh and try again.

(Click [OK] to simulate success for local testing)
    `.trim();

    const success = window.confirm(message);
    if (success) {
      window.location.href = `${window.location.origin}/?payment=success&tier=${priceId}`;
    } else {
      window.location.href = `${window.location.origin}/?payment=cancel`;
    }
  }, 100);
}
